import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import ApiRequest from "../../helper/ApiRequest";
import { BaseUrl } from "../../helper/BaseUrl";
import CustomSelect from "../../component/CustomSelect";
import CustomImageUpload from "../../component/CustomImageUpload";
import DynamicCKEditor from "../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";

const blogEditEndpoint = ApiEndpoints.aiBlog.blogEdit;
const blogUpdateEndpoint = ApiEndpoints.aiBlog.blogUpdate;

export default function EditAiBlog() {
  const navigate = useNavigate();
  const { aiblog_id } = useParams();

  const [previews, setPreviews] = useState({ thumbnail: null, banner_image: null, image1: null, image2: null });

  const [postData, setPostData] = useState({
    category_id: "", author_id: "", name: "", slug: "", thumbnail: null, alt_thumbnail: "", banner_image: null, alt_banner_image: "", heading1: "", description1: "", heading2: "", description2: "", heading3: "", description3: "", heading4: "", description4: "", heading5: "", description5: "", heading6: "", description6: "", heading7: "", description7: "", image1: null, alt_image1: "", image2: null, alt_image2: "", date: new Date(), faqs: [
      { question: "", answer: "" },
      { question: "", answer: "" },
    ]
  });

  // State variables
  const [data, setData] = useState({
    blog: {},
    categories: [],
    authors: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", `${blogEditEndpoint}${aiblog_id}`,);
        setData(response);
        setPostData(response.aiblog);
        setPreviews((prev) => ({
          ...prev,
          thumbnail: BaseUrl + response.aiblog.thumbnail || prev.thumbnail,
          banner_image: BaseUrl + response.aiblog.banner_image || prev.banner_image,
          image1: BaseUrl + response.aiblog.image1 || prev.image1,
          image2: BaseUrl + response.aiblog.image2 || prev.image2,
        }));

      } catch (error) {
        console.error("Error fetching blog data:", error);
      }
    };

    fetchData();
  }, [aiblog_id]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];

      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      let updatedValue = value;
      if (name === "date") {
        updatedValue = new Date(value).toISOString();
      }
      setPostData((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if ((value === "" || value === null)) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }
    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key === "faqs") {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    try {
      const response = await ApiRequest("POST", blogUpdateEndpoint + aiblog_id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success(response.message);
      navigate("/ai-blog");
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // 🔹 handle change for question/answer
  const handleFaqChange = (index, field, value) => {
    setPostData((prev) => {
      const updatedFaqs = [...prev.faqs];
      updatedFaqs[index][field] = value;
      return { ...prev, faqs: updatedFaqs };
    });
  };

  // 🔹 add new FAQ
  const addFaq = () => {
    setPostData((prev) => ({
      ...prev,
      faqs: [...prev.faqs, { question: "", answer: "" }],
    }));
  };

  // 🔹 remove FAQ
  const removeFaq = (index) => {
    setPostData((prev) => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index),
    }));
  };

  const pagename = {
    name: 'Blog Management',
    user: 'Admin',
    innerpage: 'Blog Management / Blog / Edit Blog'
  }

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="aiblogEdit" onSubmit={handleSubmit}>

          <div className="add-content-wrap">
            <h5>Title Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>ai blog title<small>*</small></label>
                  </div>
                  <input value={postData.name} onChange={handleChange} name="name" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input value={postData.slug} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>category<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData.category_id} onChange={handleChange} name={"category_id"} options={data?.categories} placeholder="Select Category" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Date<small>*</small></label>
                  </div>
                  <input value={postData.date ? new Date(postData.date).toISOString().split("T")[0] : ""} onChange={handleChange} name="date" type="date" className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Author Name<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData.author_id} onChange={handleChange} name="author_id" options={data?.authors} placeholder="Select Author Name" />
                </div>
              </div>

            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Content Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 1<small>*</small></label>
                  </div>
                  <input value={postData.heading1} onChange={handleChange} name="heading1" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description1<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description1} onChange={(event, editor) => handleEditorChange("description1", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 2<small>*</small></label>
                  </div>
                  <input value={postData.heading2} onChange={handleChange} name="heading2" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description2<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description2} onChange={(event, editor) => handleEditorChange("description2", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 3<small>*</small></label>
                  </div>
                  <input value={postData.heading3} onChange={handleChange} name="heading3" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description3<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description3} onChange={(event, editor) => handleEditorChange("description3", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 4<small>*</small></label>
                  </div>
                  <input value={postData.heading4} onChange={handleChange} name="heading4" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description4<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description4} onChange={(event, editor) => handleEditorChange("description4", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 5<small>*</small></label>
                  </div>
                  <input value={postData.heading5} onChange={handleChange} name="heading5" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description5<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description5} onChange={(event, editor) => handleEditorChange("description5", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 6<small>*</small></label>
                  </div>
                  <input value={postData.heading6} onChange={handleChange} name="heading6" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description6<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description6} onChange={(event, editor) => handleEditorChange("description6", editor.getData())} />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>heading 7<small>*</small></label>
                  </div>
                  <input value={postData.heading7} onChange={handleChange} name="heading7" type="text" className="form-control" placeholder="Enter Heading" />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description7<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description7} onChange={(event, editor) => handleEditorChange("description7", editor.getData())} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Image Section</h5>
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Banner Image (1608 × 600)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="banner_image"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.banner_image}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Inner banner<small>*</small></label>
                  </div>
                  <input value={postData.alt_banner_image} onChange={handleChange} name="alt_banner_image" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail (444 × 332)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.thumbnail} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Thumbnail<small>*</small></label>
                  </div>
                  <input value={postData.alt_thumbnail} onChange={handleChange} name="alt_thumbnail" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>First Inner Image (1060 × 500)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="image1"
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.image1}
                  />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For First Inner Image<small>*</small></label>
                  </div>
                  <input value={postData.alt_image1} onChange={handleChange} name="alt_image1" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Second Inner Image (1060 × 500)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="image2"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.image2} />
                </div>

                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Alt For Second Inner Image<small>*</small></label>
                  </div>
                  <input value={postData.alt_image2} onChange={handleChange} name="alt_image2" type="text" className="form-control" placeholder="Enter Alt Tag" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <h5>Faq Section</h5>
            <div className="row">

              {postData.faqs.map((faq, index) => (
                <div key={index} className="col-lg-6" style={{ position: "relative" }}>
                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Question<small>*</small></label>
                    </div>
                    <input required className="form-control" type="text" placeholder={`Question ${index + 1}`} value={faq.question} onChange={(e) => handleFaqChange(index, "question", e.target.value)} />
                  </div>

                  <div className="add-page-inputs input-add-image">
                    <div className="label-wrap-add">
                      <label>Answer<small>*</small></label>
                    </div>
                    <input required className="form-control" placeholder={`Answer ${index + 1}`}
                      value={faq.answer} onChange={(e) => handleFaqChange(index, "answer", e.target.value)} />
                  </div>
                  {index >= 2 && (
                    <button type="button" style={{ position: "absolute", background: "linear-gradient(270deg,#e20814,#a80008)", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "12px", padding: "3px 15px" }} onClick={() => removeFaq(index)}>
                      REMOVE
                    </button>
                  )}
                </div>
              ))}

              <div className="col-lg-12 text-center">
                <button type="button" className="add-faqs" onClick={addFaq} style={{ background: "green", top: "5px", right: "10px", color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", padding: "5px 15px", marginTop: "15px" }}>
                  Add New Faq
                </button>
              </div>
            </div>
          </div>
        </form>

        <button type="submit" form="aiblogEdit" className="default-btn">publish <span></span></button>
      </div>
    </>
  );
}
